import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { motion } from "framer-motion";
import { Badge } from "../components/ui/badge";
import { Mail, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";

const faqCategories = [
  {
    category: "Getting Started",
    questions: [
      {
        question: "What is CannaBias?",
        answer: "CannaBias is a comprehensive cannabis wellness platform that combines education, community support, and health tracking. We provide a safe, Cyber Essentials aligned space for users to learn about cannabis, connect with healthcare professionals, and track their wellness journey."
      },
      {
        question: "How do I create an account?",
        answer: "Creating an account is simple and secure. Click 'Get Started' on our homepage, enter your email address, and we'll send you a magic link. Click the link in your email to instantly sign in - no password required! We use passwordless authentication to keep your account secure."
      },
      {
        question: "Is CannaBias free to use?",
        answer: "Yes! We offer a free forever plan that includes access to our community, basic tracking features, and educational content. We also offer premium plans with advanced analytics, personalized recommendations, and priority support from healthcare professionals."
      }
    ]
  },
  {
    category: "Privacy & Security",
    questions: [
      {
        question: "How is my data protected?",
        answer: "We take your privacy seriously. CannaBias aligns with Cyber Essentials standards and uses bank-level encryption (256-bit SSL) to protect all your data. Your health information is encrypted both in transit and at rest, and we never share your personal data with third parties without your explicit consent."
      },
      {
        question: "Who can see my information?",
        answer: "Your personal health information is completely private by default. You choose what to share and with whom. Community posts can be anonymous, and your tracking data is only visible to you unless you explicitly choose to share it with healthcare providers within the platform."
      },
      {
        question: "Can I delete my account and data?",
        answer: "Absolutely. You have complete control over your data. You can download all your information at any time, and if you choose to delete your account, all your personal data will be permanently removed from our systems within 30 days, in compliance with GDPR and CCPA regulations."
      }
    ]
  },
  {
    category: "Features & Usage",
    questions: [
      {
        question: "What can I track on CannaBias?",
        answer: "Our wellness tracking features allow you to log product usage, dosage, timing, and effects. You can track symptoms, mood, sleep quality, and other wellness metrics. The platform provides visualizations and insights to help you understand patterns and optimize your cannabis use."
      },
      {
        question: "Can I connect with healthcare professionals?",
        answer: "Yes! We have verified healthcare professionals who specialize in cannabis medicine. Premium members can schedule consultations, get personalized recommendations, and receive ongoing support from licensed practitioners. All healthcare providers on our platform are thoroughly vetted and verified."
      },
      {
        question: "Do you ship cannabis products?",
        answer: "No, CannaBias does not sell or ship cannabis products. We are an educational and wellness tracking platform. However, we do provide information about verified, lab-tested products and licensed dispensaries in your area, in compliance with local laws."
      }
    ]
  },
  {
    category: "Medical Claims & Compliance",
    questions: [
      {
        question: "Does CannaBias make medical claims?",
        answer: "No. CannaBias does not make medical claims about cannabis or any specific products. We provide educational content based on peer-reviewed research and allow users to track their personal experiences. Always consult with a qualified healthcare provider before starting any new wellness regimen."
      },
      {
        question: "Is this platform a substitute for medical advice?",
        answer: "No. CannaBias is not a substitute for professional medical advice, diagnosis, or treatment. While we connect you with healthcare professionals, all information on the platform is for educational purposes. Always seek the advice of your physician or qualified health provider with any questions about medical conditions."
      },
      {
        question: "Are you compliant with state laws?",
        answer: "Yes. We operate in full compliance with applicable state and federal laws. We verify user eligibility based on their location and local regulations. Our platform does not facilitate any illegal activities and we maintain strict age verification protocols."
      }
    ]
  },
  {
    category: "Community & Support",
    questions: [
      {
        question: "How is the community moderated?",
        answer: "Our community is actively moderated by healthcare professionals and trained moderators 24/7. We have strict community guidelines that prohibit misinformation, harassment, and illegal activities. All posts are reviewed to ensure they meet our standards for accuracy and respectfulness."
      },
      {
        question: "Can I remain anonymous in the community?",
        answer: "Yes! You can participate in community discussions using a username without revealing your real identity. Your privacy is important to us, and we've designed the platform to allow meaningful connections while protecting your anonymity."
      },
      {
        question: "How do I report inappropriate content?",
        answer: "Every post and comment has a report button. Click it to flag content that violates our community guidelines. Our moderation team reviews all reports within 2 hours and takes appropriate action. We take user safety seriously and appreciate your help in keeping our community safe."
      }
    ]
  }
];

export function FAQ() {
  return (
    <div className="min-h-screen pt-32 pb-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div 
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Badge variant="secondary" className="mb-4">
            Help Centre
          </Badge>
          <h1 className="mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-muted-foreground">
            Find answers to common questions about CannaBias
          </p>
        </motion.div>

        {/* FAQ Categories */}
        <motion.div 
          className="space-y-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          {faqCategories.map((category, categoryIndex) => (
            <div key={categoryIndex} className="space-y-4">
              <h2 className="text-2xl mb-6 pb-2 border-b border-border">
                {category.category}
              </h2>
              <Accordion type="single" collapsible className="space-y-4">
                {category.questions.map((item, questionIndex) => (
                  <AccordionItem 
                    key={questionIndex} 
                    value={`item-${categoryIndex}-${questionIndex}`}
                    className="border border-border rounded-lg px-6 bg-card hover:border-primary/30 transition-colors"
                  >
                    <AccordionTrigger className="text-left hover:no-underline py-6">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-6 pt-0 leading-relaxed">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </motion.div>

        {/* Contact CTA */}
        <motion.div 
          className="mt-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <Card className="p-8 text-center">
            <h3 className="mb-2">Still have questions?</h3>
            <p className="text-muted-foreground mb-6">
              Our support team is here to help you 24/7
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild size="lg" className="gap-2">
                <a href="mailto:support@cannabias.co.uk">
                  <Mail className="w-4 h-4" />
                  Email Support
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="gap-2">
                <a href="#">
                  <MessageCircle className="w-4 h-4" />
                  Join Community
                </a>
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}